// src/api/features/locations/api.ts
import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  ApiEnvelope,
  ApiMessage,
  PaginatedCountries,
  PaginatedRegions,
  PaginatedCities,
  PaginatedCityAliases,
  LocationCountry,
  LocationRegion,
  LocationCity,
  LocationCityAlias,
  ListCountriesQuery,
  ListRegionsQuery,
  ListCitiesQuery,
  ListCityAliasesQuery,
  ResolveCityInput,
  ResolveCityResult,
  EnsureLocationTreeBody,
  EnsureLocationTreeResult,
  CreateCountryBody,
  UpdateCountryBody,
  CreateRegionBody,
  UpdateRegionBody,
  CreateCityBody,
  UpdateCityBody,
  CreateCityAliasBody,
  UpdateCityAliasBody,
} from "./types";

/* =========================
 * Helpers (same style as leads)
 * ========================= */

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function unwrapMessage(res: any): ApiMessage {
  if (res && typeof res === "object" && "ok" in res) {
    const r = res as ApiMessage;
    return { ok: Boolean(r.ok), message: r.message, meta: (r as any).meta };
  }
  return { ok: true, message: undefined };
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }

  const s = u.toString();
  return s ? `?${s}` : "";
}

function safePage(raw: unknown): number {
  const n = Number(raw ?? 1);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function safeLimit(raw: unknown): number {
  const n = Number(raw ?? 20);
  if (!Number.isFinite(n) || n <= 0) return 20;
  return Math.min(100, n);
}

function normIso2(v: unknown): string {
  return String(v ?? "").trim().toUpperCase();
}

function trimStr(v: unknown): string {
  return String(v ?? "").trim();
}

/* =========================
 * URLs
 * ========================= */

const LOCATIONS_BASE = PATHS.locations.base;

/* =========================
 * Public — Countries / Regions / Cities (dropdown flow)
 * (3 GET endpoints)
 * ========================= */

export async function listCountries(
  query: ListCountriesQuery = {}
): Promise<PaginatedCountries> {
  const page = safePage(query.page);
  const limit = safeLimit(query.limit);

  const url = `${LOCATIONS_BASE}/countries${qs({
    page,
    limit,
    q: query.q?.trim() || undefined,
    iso2: query.iso2 ? normIso2(query.iso2) : undefined,
    name: query.name?.trim() || undefined,
    includeDeleted: query.includeDeleted ? "true" : undefined,
  })}`;

  const res = await request<
    | (ApiEnvelope<LocationCountry[]> & { meta?: any })
    | { items: LocationCountry[]; page: number; pageSize: number; total: number }
  >({
    method: "GET",
    url,
  });

  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<LocationCountry[]> & { meta?: any };
    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? page),
      pageSize: Number(meta.pageSize ?? limit),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  return res as PaginatedCountries;
}

export async function listRegions(query: ListRegionsQuery): Promise<PaginatedRegions> {
  const page = safePage(query.page);
  const limit = safeLimit(query.limit);

  const countryIso2 = normIso2(query.countryIso2);
  if (!countryIso2) throw new Error("countryIso2 is required");

  const url = `${LOCATIONS_BASE}/regions${qs({
    page,
    limit,
    countryIso2,
    q: query.q?.trim() || undefined,
    code: query.code?.trim() || undefined,
    name: (query as any).name?.trim?.() || undefined,
    includeDeleted: (query as any).includeDeleted ? "true" : undefined,
  })}`;

  const res = await request<
    | (ApiEnvelope<LocationRegion[]> & { meta?: any })
    | { items: LocationRegion[]; page: number; pageSize: number; total: number }
  >({
    method: "GET",
    url,
  });

  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<LocationRegion[]> & { meta?: any };
    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? page),
      pageSize: Number(meta.pageSize ?? limit),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  return res as PaginatedRegions;
}

export async function listCities(query: ListCitiesQuery): Promise<PaginatedCities> {
  const page = safePage(query.page);
  const limit = safeLimit(query.limit);

  const countryIso2 = normIso2(query.countryIso2);
  if (!countryIso2) throw new Error("countryIso2 is required");

  const url = `${LOCATIONS_BASE}/cities${qs({
    page,
    limit,
    countryIso2,
    regionId:
      query.regionId === null
        ? "null"
        : typeof query.regionId === "number" && Number.isFinite(query.regionId)
          ? query.regionId
          : undefined,
    q: query.q?.trim() || undefined,
    name: (query as any).name?.trim?.() || undefined,
    nameNorm: (query as any).nameNorm?.trim?.() || undefined,
    includeDeleted: (query as any).includeDeleted ? "true" : undefined,
  })}`;

  const res = await request<
    | (ApiEnvelope<LocationCity[]> & { meta?: any })
    | { items: LocationCity[]; page: number; pageSize: number; total: number }
  >({
    method: "GET",
    url,
  });

  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<LocationCity[]> & { meta?: any };
    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? page),
      pageSize: Number(meta.pageSize ?? limit),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  return res as PaginatedCities;
}

/* =========================
 * Public — Resolve City
 * (1 POST endpoint)
 * ========================= */

export async function resolveCity(body: ResolveCityInput): Promise<ResolveCityResult> {
  const dto: any = { ...body };

  if (dto?.countryIso2 != null) dto.countryIso2 = normIso2(dto.countryIso2);
  if (dto?.cityName != null) dto.cityName = trimStr(dto.cityName);

  if (dto?.regionName != null) dto.regionName = trimStr(dto.regionName);
  if (dto?.regionCode != null) dto.regionCode = trimStr(dto.regionCode);

  // Public flow: do not send allowCreate
  if ("allowCreate" in dto) delete dto.allowCreate;
  // FE should also not send actor; backend sets it from auth context (or "Public")
  if ("actor" in dto) delete dto.actor;

  const res = await request<ResolveCityResult | ApiEnvelope<ResolveCityResult>>({
    method: "POST",
    url: `${LOCATIONS_BASE}/resolve-city`,
    data: dto,
  });

  return unwrap<ResolveCityResult>(res);
}

/* =========================
 * Admin — Ensure Location Tree
 * (1 POST endpoint)
 * ========================= */

export async function ensureLocationTree(
  body: EnsureLocationTreeBody
): Promise<EnsureLocationTreeResult> {
  const dto: any = { ...body };

  if (dto?.country?.iso2 != null) dto.country.iso2 = normIso2(dto.country.iso2);

  if (dto?.country?.name != null) dto.country.name = trimStr(dto.country.name);

  if (dto?.region?.name != null) dto.region.name = trimStr(dto.region.name);
  if (dto?.region?.code != null) dto.region.code = trimStr(dto.region.code);

  if (dto?.city?.name != null) dto.city.name = trimStr(dto.city.name);

  if (dto?.alias?.alias != null) dto.alias.alias = trimStr(dto.alias.alias);

  const res = await request<
    EnsureLocationTreeResult | ApiEnvelope<EnsureLocationTreeResult>
  >({
    method: "POST",
    url: `${LOCATIONS_BASE}/ensure`,
    data: dto,
  });

  return unwrap<EnsureLocationTreeResult>(res);
}

/* =========================
 * Admin — Single record lookups
 * (back the create/edit screens, which must load from the URL alone)
 * ========================= */

export async function getCountry(iso2: string): Promise<LocationCountry> {
  const key = normIso2(iso2);

  const res = await request<LocationCountry | ApiEnvelope<LocationCountry>>({
    method: "GET",
    url: `${LOCATIONS_BASE}/countries/${encodeURIComponent(key)}`,
  });

  return unwrap<LocationCountry>(res);
}

export async function getRegion(id: number): Promise<LocationRegion> {
  const res = await request<LocationRegion | ApiEnvelope<LocationRegion>>({
    method: "GET",
    url: `${LOCATIONS_BASE}/regions/${encodeURIComponent(String(id))}`,
  });

  return unwrap<LocationRegion>(res);
}

export async function getCity(id: number): Promise<LocationCity> {
  const res = await request<LocationCity | ApiEnvelope<LocationCity>>({
    method: "GET",
    url: `${LOCATIONS_BASE}/cities/${encodeURIComponent(String(id))}`,
  });

  return unwrap<LocationCity>(res);
}

/* =========================
 * Admin — Countries CRUD
 * ========================= */

export async function createCountry(body: CreateCountryBody): Promise<LocationCountry> {
  const dto: any = { ...body };
  dto.iso2 = normIso2(dto.iso2);
  dto.name = trimStr(dto.name);
  if (dto.nameNorm != null) dto.nameNorm = trimStr(dto.nameNorm);

  const res = await request<LocationCountry | ApiEnvelope<LocationCountry>>({
    method: "POST",
    url: `${LOCATIONS_BASE}/countries`,
    data: dto,
  });

  return unwrap<LocationCountry>(res);
}

export async function updateCountry(
  iso2: string,
  body: UpdateCountryBody
): Promise<LocationCountry> {
  const key = normIso2(iso2);

  const dto: any = { ...body };
  if (dto.name != null) dto.name = trimStr(dto.name);
  if (dto.nameNorm != null) dto.nameNorm = trimStr(dto.nameNorm);

  const res = await request<LocationCountry | ApiEnvelope<LocationCountry>>({
    method: "PUT",
    url: `${LOCATIONS_BASE}/countries/${encodeURIComponent(key)}`,
    data: dto,
  });

  return unwrap<LocationCountry>(res);
}

export async function deleteCountry(iso2: string): Promise<ApiMessage> {
  const key = normIso2(iso2);

  const res = await request<ApiMessage>({
    method: "DELETE",
    url: `${LOCATIONS_BASE}/countries/${encodeURIComponent(key)}`,
  });

  return unwrapMessage(res);
}

/* =========================
 * Admin — Regions CRUD
 * ========================= */

export async function createRegion(body: CreateRegionBody): Promise<LocationRegion> {
  const dto: any = { ...body };
  dto.countryIso2 = normIso2(dto.countryIso2);
  dto.name = trimStr(dto.name);
  if (dto.nameNorm != null) dto.nameNorm = trimStr(dto.nameNorm);
  if (dto.code != null) dto.code = trimStr(dto.code);

  const res = await request<LocationRegion | ApiEnvelope<LocationRegion>>({
    method: "POST",
    url: `${LOCATIONS_BASE}/regions`,
    data: dto,
  });

  return unwrap<LocationRegion>(res);
}

export async function updateRegion(
  id: number,
  body: UpdateRegionBody
): Promise<LocationRegion> {
  const dto: any = { ...body };
  if (dto.countryIso2 != null) dto.countryIso2 = normIso2(dto.countryIso2);
  if (dto.name != null) dto.name = trimStr(dto.name);
  if (dto.nameNorm != null) dto.nameNorm = trimStr(dto.nameNorm);
  if (dto.code != null) dto.code = trimStr(dto.code);

  const res = await request<LocationRegion | ApiEnvelope<LocationRegion>>({
    method: "PUT",
    url: `${LOCATIONS_BASE}/regions/${encodeURIComponent(String(id))}`,
    data: dto,
  });

  return unwrap<LocationRegion>(res);
}

export async function deleteRegion(id: number): Promise<ApiMessage> {
  const res = await request<ApiMessage>({
    method: "DELETE",
    url: `${LOCATIONS_BASE}/regions/${encodeURIComponent(String(id))}`,
  });

  return unwrapMessage(res);
}

/* =========================
 * Admin — Cities CRUD
 * ========================= */

export async function createCity(body: CreateCityBody): Promise<LocationCity> {
  const dto: any = { ...body };
  dto.countryIso2 = normIso2(dto.countryIso2);
  dto.name = trimStr(dto.name);

  if (dto.nameNorm != null) dto.nameNorm = trimStr(dto.nameNorm);

  // regionId: allow undefined, null, or number
  if (dto.regionId !== undefined && dto.regionId !== null) {
    const n = Number(dto.regionId);
    dto.regionId = Number.isFinite(n) ? n : null;
  }

  if (dto.latitude != null) dto.latitude = trimStr(dto.latitude);
  if (dto.longitude != null) dto.longitude = trimStr(dto.longitude);

  const res = await request<LocationCity | ApiEnvelope<LocationCity>>({
    method: "POST",
    url: `${LOCATIONS_BASE}/cities`,
    data: dto,
  });

  return unwrap<LocationCity>(res);
}

export async function updateCity(id: number, body: UpdateCityBody): Promise<LocationCity> {
  const dto: any = { ...body };

  if (dto.countryIso2 != null) dto.countryIso2 = normIso2(dto.countryIso2);
  if (dto.name != null) dto.name = trimStr(dto.name);
  if (dto.nameNorm != null) dto.nameNorm = trimStr(dto.nameNorm);

  if (dto.regionId !== undefined) {
    if (dto.regionId === null) dto.regionId = null;
    else {
      const n = Number(dto.regionId);
      dto.regionId = Number.isFinite(n) ? n : null;
    }
  }

  if (dto.latitude !== undefined) dto.latitude = dto.latitude === null ? null : trimStr(dto.latitude);
  if (dto.longitude !== undefined) dto.longitude = dto.longitude === null ? null : trimStr(dto.longitude);

  const res = await request<LocationCity | ApiEnvelope<LocationCity>>({
    method: "PUT",
    url: `${LOCATIONS_BASE}/cities/${encodeURIComponent(String(id))}`,
    data: dto,
  });

  return unwrap<LocationCity>(res);
}

export async function deleteCity(id: number): Promise<ApiMessage> {
  const res = await request<ApiMessage>({
    method: "DELETE",
    url: `${LOCATIONS_BASE}/cities/${encodeURIComponent(String(id))}`,
  });

  return unwrapMessage(res);
}

/* =========================
 * Admin — City Aliases CRUD
 * ========================= */

export async function listCityAliases(
  query: ListCityAliasesQuery = {}
): Promise<PaginatedCityAliases> {
  const page = safePage(query.page);
  const limit = safeLimit(query.limit);

  const url = `${LOCATIONS_BASE}/city-aliases${qs({
    page,
    limit,
    q: query.q?.trim() || undefined,
    cityId:
      query.cityId !== undefined && query.cityId !== null
        ? Number(query.cityId)
        : undefined,
    alias: query.alias?.trim() || undefined,
    aliasNorm: query.aliasNorm?.trim() || undefined,
    includeDeleted: query.includeDeleted ? "true" : undefined,
    // countryIso2 is optional; backend service may not use it directly for search
    countryIso2: query.countryIso2 ? normIso2(query.countryIso2) : undefined,
  })}`;

  const res = await request<
    | (ApiEnvelope<LocationCityAlias[]> & { meta?: any })
    | { items: LocationCityAlias[]; page: number; pageSize: number; total: number }
  >({
    method: "GET",
    url,
  });

  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const r = res as ApiEnvelope<LocationCityAlias[]> & { meta?: any };
    const items = Array.isArray(r.data) ? r.data : [];
    const meta = r.meta ?? {};

    return {
      items,
      page: Number(meta.page ?? page),
      pageSize: Number(meta.pageSize ?? limit),
      total: Number(meta.total ?? items.length ?? 0),
    };
  }

  return res as PaginatedCityAliases;
}

export async function createCityAlias(
  body: CreateCityAliasBody
): Promise<LocationCityAlias> {
  const dto: any = { ...body };
  dto.cityId = Number(dto.cityId);
  dto.alias = trimStr(dto.alias);
  if (dto.aliasNorm != null) dto.aliasNorm = trimStr(dto.aliasNorm);

  const res = await request<LocationCityAlias | ApiEnvelope<LocationCityAlias>>({
    method: "POST",
    url: `${LOCATIONS_BASE}/city-aliases`,
    data: dto,
  });

  return unwrap<LocationCityAlias>(res);
}

export async function updateCityAlias(
  id: number,
  body: UpdateCityAliasBody
): Promise<LocationCityAlias> {
  const dto: any = { ...body };

  if (dto.cityId !== undefined) dto.cityId = Number(dto.cityId);
  if (dto.alias !== undefined) dto.alias = dto.alias === null ? null : trimStr(dto.alias);
  if (dto.aliasNorm !== undefined)
    dto.aliasNorm = dto.aliasNorm === null ? null : trimStr(dto.aliasNorm);

  const res = await request<LocationCityAlias | ApiEnvelope<LocationCityAlias>>({
    method: "PUT",
    url: `${LOCATIONS_BASE}/city-aliases/${encodeURIComponent(String(id))}`,
    data: dto,
  });

  return unwrap<LocationCityAlias>(res);
}

export async function deleteCityAlias(id: number): Promise<ApiMessage> {
  const res = await request<ApiMessage>({
    method: "DELETE",
    url: `${LOCATIONS_BASE}/city-aliases/${encodeURIComponent(String(id))}`,
  });

  return unwrapMessage(res);
}
